import { useEffect, useRef, useState } from "react";
import { Mic, Square, Upload, Loader2, Trash2, Sparkles, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { slidesVoiceClone, slidesVoiceDelete } from "@/lib/api";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** Step 3 add-on: clone the dealer's own voice (ElevenLabs IVC). Private — only this dealer sees it. */
export default function VoiceClonePanel({ userRecordId, cfg, mine, onCloned, onDeleted, onUse }) {
  const [samples, setSamples] = useState([]);           // File/Blob list
  const [rec, setRec] = useState(false);
  const [secs, setSecs] = useState(0);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const fileRef = useRef(null);
  const cost = cfg?.costs?.clone;

  useEffect(() => () => { clearInterval(timer.current); recRef.current?.state === "recording" && recRef.current.stop(); }, []);

  async function startRec() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? { mimeType: "audio/webm;codecs=opus" } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
        blob.name = `recording-${Date.now()}.webm`;
        setSamples((s) => [...s, blob]);
      };
      mr.start(1000);
      recRef.current = mr;
      setRec(true); setSecs(0);
      timer.current = setInterval(() => setSecs((v) => v + 1), 1000);
    } catch { toast.error("Microphone not available — allow mic access or upload a file instead"); }
  }
  function stopRec() { clearInterval(timer.current); recRef.current?.stop(); setRec(false); }

  async function clone() {
    if (!samples.length) return toast.error("Record or upload at least 30 seconds of your voice");
    if (!consent) return toast.error("Please tick the consent box");
    setBusy(true);
    try {
      const r = await slidesVoiceClone(userRecordId, { name: cfg?.branding_defaults?.owner_name ? `${cfg.branding_defaults.owner_name}'s voice` : "", consent, files: samples });
      toast.success(`Your voice is ready — “${r.voice.name}” is now in your voice list`);
      setSamples([]); setConsent(false);
      onCloned?.(r.voice, r.balance);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clone the voice"); }
    finally { setBusy(false); }
  }
  async function remove(v) {
    if (!window.confirm(`Delete “${v.name}”? Videos using it switch back to Melissa.`)) return;
    setBusy(true);
    try { await slidesVoiceDelete(userRecordId, v.voice_id); toast.success("Voice deleted"); onDeleted?.(v.voice_id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 space-y-2" data-testid="slides-voice-clone">
      <div className="text-xs font-bold text-[#0b2545] flex items-center gap-1.5"><Sparkles size={12} className="text-[#c9a34a]" /> My own voice <span className="font-normal text-zinc-500">· private to your account, nobody else can see or use it</span></div>
      {mine.length > 0 && (
        <ul className="space-y-1" data-testid="slides-my-voices">
          {mine.map((v) => (
            <li key={v.voice_id} className="flex flex-wrap items-center gap-2 text-xs bg-white rounded-lg border border-zinc-200 px-2.5 py-1.5" data-testid={`slides-my-voice-${v.voice_id}`}>
              <Mic size={12} className="text-emerald-600" /> <b>{v.name}</b>
              <button type="button" onClick={() => onUse?.(v.voice_id)} className="text-violet-700 hover:underline inline-flex items-center gap-1" data-testid={`slides-my-voice-use-${v.voice_id}`}><Play size={10} /> Use this voice</button>
              <button type="button" onClick={() => remove(v)} disabled={busy} className="ml-auto text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`slides-my-voice-delete-${v.voice_id}`}><Trash2 size={10} /> Delete</button>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[11px] text-zinc-600">Read anything naturally for <b>1–3 minutes</b> in a quiet room (30 s minimum) — a product description works well. Then clone{cost ? ` (${cost} AI points)` : ""}.</div>
      <div className="flex flex-wrap items-center gap-2">
        {!rec ? (
          <Button type="button" size="sm" variant="outline" className="rounded-lg h-9" onClick={startRec} disabled={busy} data-testid="slides-voice-record"><Mic size={13} className="mr-1 text-rose-600" /> Record</Button>
        ) : (
          <Button type="button" size="sm" className="rounded-lg h-9 bg-rose-600 hover:bg-rose-500 text-white" onClick={stopRec} data-testid="slides-voice-stop"><Square size={11} className="mr-1" fill="currentColor" /> Stop · {fmt(secs)}</Button>
        )}
        <input ref={fileRef} type="file" accept="audio/*,video/webm,video/mp4" multiple className="hidden" onChange={(e) => { setSamples((s) => [...s, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="slides-voice-file" />
        <Button type="button" size="sm" variant="outline" className="rounded-lg h-9" onClick={() => fileRef.current?.click()} disabled={busy || rec} data-testid="slides-voice-upload"><Upload size={13} className="mr-1" /> Upload audio</Button>
        <Button type="button" size="sm" className="rounded-lg h-9 bg-[#0b2545] hover:bg-[#143a6b] text-white" onClick={clone} disabled={busy || rec || !samples.length} data-testid="slides-voice-clone-btn">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />} Clone my voice</Button>
      </div>
      {samples.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 text-[11px]" data-testid="slides-voice-samples">
          {samples.map((f, i) => (
            <li key={i} className="inline-flex items-center gap-1 rounded-full bg-white border border-zinc-200 px-2 py-0.5">
              {f.name || `sample ${i + 1}`} · {(f.size / 1024).toFixed(0)} KB
              <button type="button" onClick={() => setSamples((s) => s.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-rose-600" data-testid={`slides-voice-sample-remove-${i}`}>×</button>
            </li>
          ))}
        </ul>
      )}
      <label className="flex items-start gap-1.5 text-[11px] text-zinc-600">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="slides-voice-consent" />
        I confirm this is my own voice (or I have the speaker's permission) and I agree it may be used to narrate my videos.
      </label>
    </div>
  );
}
